import React from 'react';
import Icon from '../../../components/AppIcon';
import { format } from 'date-fns';

const RealtimeEventStream = ({ events, onAcknowledge }) => {
  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'warning': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'info': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getEventIcon = (eventType) => {
    switch (eventType) {
      case 'USER_SIGNUP': return 'user-plus';
      case 'PREMIUM_CONVERSION': return 'trending-up';
      case 'PAYMENT_FAILURE': return 'credit-card';
      case 'SYSTEM_ANOMALY': return 'alert-octagon';
      case 'FEATURE_SPIKE': return 'activity';
      default: return 'bell';
    }
  };

  const getEventLabel = (eventType) => {
    switch (eventType) {
      case 'USER_SIGNUP': return 'New User Signup';
      case 'PREMIUM_CONVERSION': return 'Premium Conversion';
      case 'PAYMENT_FAILURE': return 'Payment Failure';
      case 'SYSTEM_ANOMALY': return 'System Anomaly';
      case 'FEATURE_SPIKE': return 'Feature Usage Spike';
      default: return eventType;
    }
  };

  const getEventIconColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical': return 'text-red-600';
      case 'warning': return 'text-yellow-600';
      case 'info': return 'text-blue-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
      <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Icon name="activity" className="w-5 h-5 text-indigo-600" />
            <h2 className="text-lg font-semibold text-gray-900">Real-time Event Stream</h2>
          </div>
          <span className="text-sm text-gray-600">{events?.length || 0} events</span>
        </div>
      </div>
      <div className="max-h-[600px] overflow-y-auto">
        {!events || events?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Icon name="inbox" className="w-12 h-12 text-gray-400 mb-3" />
            <p className="text-gray-500">No events to display</p>
            <p className="text-sm text-gray-400 mt-1">Events will appear here in real-time</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {events?.map((event) => (
              <div
                key={event?.id}
                className={`px-6 py-4 hover:bg-gray-50 transition-colors ${
                  event?.acknowledged ? 'opacity-60 bg-gray-50' : ''
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    event?.eventSeverity === 'critical' ? 'bg-red-100' :
                    event?.eventSeverity === 'warning' ? 'bg-yellow-100' : 'bg-blue-100'
                  }`}>
                    <Icon 
                      name={getEventIcon(event?.eventType)} 
                      className={`w-5 h-5 ${getEventIconColor(event?.eventSeverity)}`}
                    />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">
                          {getEventLabel(event?.eventType)}
                        </h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {event?.createdAt ? format(new Date(event?.createdAt), 'MMM dd, yyyy HH:mm:ss') : 'N/A'}
                        </p>
                      </div>
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${getSeverityColor(event?.eventSeverity)}`}>
                        {event?.eventSeverity?.toUpperCase()}
                      </span>
                    </div>

                    {event?.eventCategory && (
                      <p className="text-sm text-gray-700 mb-2">{event?.eventCategory}</p>
                    )}

                    {event?.eventData && Object.keys(event?.eventData)?.length > 0 && (
                      <div className="bg-gray-50 rounded-lg p-3 mb-3 border border-gray-200">
                        <div className="space-y-1">
                          {Object.entries(event?.eventData)?.slice(0, 5)?.map(([key, value]) => (
                            <div key={key} className="flex items-start gap-2">
                              <span className="text-xs font-medium text-gray-600 min-w-[120px]">
                                {key?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}:
                              </span>
                              <span className="text-xs text-gray-800 font-mono">
                                {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {!event?.acknowledged && onAcknowledge && (
                      <button
                        onClick={() => onAcknowledge(event?.id)}
                        className="text-xs text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1"
                      >
                        <Icon name="check" className="w-3 h-3" />
                        Acknowledge
                      </button>
                    )}

                    {event?.acknowledged && (
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <Icon name="check-circle" className="w-3 h-3 text-green-600" />
                        <span>Acknowledged {event?.acknowledgedAt ? format(new Date(event?.acknowledgedAt), 'MMM dd, HH:mm') : ''}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RealtimeEventStream;